import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { getStoredToken } from '../auth/firebase';
import './AdminPanel.css';

const API_BASE = process.env.REACT_APP_API_URL || '';

/* ───────────────────────── tiny bar-chart ───────────────────────── */
function MiniChart({ data, labelKey = 'date', valueKey = 'count', color = '#4a8a73', height = 80 }) {
  if (!data || data.length === 0) return <div className="ad-chart-empty">No data</div>;
  const max = Math.max(...data.map(d => d[valueKey]), 1);
  return (
    <div className="ad-chart" style={{ height }}>
      {data.map((d, i) => (
        <div key={i} className="ad-chart-bar-wrap" title={`${d[labelKey]}: ${d[valueKey]}`}>
          <div
            className="ad-chart-bar"
            style={{ height: `${(d[valueKey] / max) * 100}%`, background: color }}
          />
          <span className="ad-chart-label">{d[labelKey]?.slice(5) || ''}</span>
        </div>
      ))}
    </div>
  );
}

/* Build an area+line sparkline from 0..1 scores (oldest→newest). Returns SVG
   path strings + the last point, in a fixed viewBox that stretches to width. */
function sparkPaths(scores, w = 320, h = 64, pad = 7) {
  const vals = (scores || []).map(v => Math.max(0, Math.min(1, v || 0)));
  if (vals.length < 2) return null;
  const n = vals.length;
  const pts = vals.map((v, i) => {
    const x = Math.round((i / (n - 1)) * w * 100) / 100;
    const y = Math.round((pad + (1 - v) * (h - 2 * pad)) * 100) / 100;
    return [x, y];
  });
  const line = pts.map((p, i) => `${i ? 'L' : 'M'}${p[0]},${p[1]}`).join(' ');
  const area = `${line} L${w},${h} L0,${h} Z`;
  return { line, area, last: pts[n - 1], w, h };
}

/* ═══════════════════════ MAIN COMPONENT ═══════════════════════════ */
function AdminPanel() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  // Nav
  const [view, setView] = useState('dashboard'); // dashboard | users | system | invites

  // Data
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // User detail drawer
  const [detailUser, setDetailUser] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Modals
  const [pwModal, setPwModal] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [settingPw, setSettingPw] = useState(false);
  const [emailModal, setEmailModal] = useState(null);
  const [newEmail, setNewEmail] = useState('');
  const [settingEmail, setSettingEmail] = useState(false);
  const [togglingAdmin, setTogglingAdmin] = useState(null);

  // Invites
  const [invites, setInvites] = useState([]);
  const [invitesLoading, setInvitesLoading] = useState(false);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteMaxUses, setInviteMaxUses] = useState(1);
  const [inviteExpiresDays, setInviteExpiresDays] = useState('');
  const [inviteCreating, setInviteCreating] = useState(false);

  // Beta requests
  const [betaRequests, setBetaRequests] = useState([]);
  const [betaLoading, setBetaLoading] = useState(false);
  const [betaPending, setBetaPending] = useState(0);
  const [betaActing, setBetaActing] = useState(null);

  // Compose email
  const [emailComposeModal, setEmailComposeModal] = useState(null);
  const [composeSubject, setComposeSubject] = useState('');
  const [composeMessage, setComposeMessage] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);

  // Search & sort
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');

  // AI Quality eval
  const [evalLatest, setEvalLatest] = useState(null);
  const [evalHistory, setEvalHistory] = useState([]);
  const [evalLoading, setEvalLoading] = useState(false);
  const [evalRunning, setEvalRunning] = useState(false);
  const [healthLatest, setHealthLatest] = useState(null);
  const [healthHistory, setHealthHistory] = useState([]);
  const [healthLoading, setHealthLoading] = useState(false);
  const [healthRunning, setHealthRunning] = useState(false);

  // A/B threshold tuning
  const [tuneKnobs, setTuneKnobs] = useState(null);
  const [tuneLoading, setTuneLoading] = useState(false);

  // Faithfulness editor config
  const [editorCfg, setEditorCfg] = useState(null);
  const [editorLoading, setEditorLoading] = useState(false);
  const [editorSaving, setEditorSaving] = useState(false);
  const [editorForm, setEditorForm] = useState({ provider: 'off', model: 'claude-haiku-4-5', access_token: '', refresh_token: '', expires_in: '' });
  const [editorMsg, setEditorMsg] = useState('');
  // One-click OAuth generate flow
  const [oauthUrl, setOauthUrl] = useState('');
  const [oauthSession, setOauthSession] = useState('');
  const [oauthCode, setOauthCode] = useState('');
  const [oauthBusy, setOauthBusy] = useState(false);

  const authHeaders = useCallback(() => ({
    Authorization: `Bearer ${getStoredToken()}`,
    'Content-Type': 'application/json',
  }), []);

  /* ─── Fetch users ─── */
  const fetchUsers = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API_BASE}/admin/users`, { headers: authHeaders() });
      if (res.status === 403) { setError('Admin privileges required.'); setLoading(false); return; }
      const data = await res.json();
      setUsers(data.users || []);
    } catch (e) { setError('Failed to load users: ' + e.message); }
    setLoading(false);
  }, [authHeaders]);

  /* ─── Fetch stats ─── */
  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/admin/stats`, { headers: authHeaders() });
      if (res.ok) setStats(await res.json());
    } catch { /* silent */ }
    setStatsLoading(false);
  }, [authHeaders]);

  /* ─── Fetch AI-quality eval (latest + trend) ─── */
  const fetchEval = useCallback(async () => {
    setEvalLoading(true);
    try {
      const [lRes, hRes] = await Promise.all([
        fetch(`${API_BASE}/admin/eval/latest`, { headers: authHeaders() }),
        fetch(`${API_BASE}/admin/eval/history?limit=30`, { headers: authHeaders() }),
      ]);
      if (lRes.ok) setEvalLatest(await lRes.json());
      if (hRes.ok) { const d = await hRes.json(); setEvalHistory(d.runs || []); }
    } catch { /* silent */ }
    setEvalLoading(false);
  }, [authHeaders]);

  /* ─── Fetch System Health (current snapshot + trend) ─── */
  const fetchHealth = useCallback(async () => {
    setHealthLoading(true);
    try {
      const [lRes, hRes] = await Promise.all([
        fetch(`${API_BASE}/admin/health/latest`, { headers: authHeaders() }),
        fetch(`${API_BASE}/admin/health/history?limit=80`, { headers: authHeaders() }),
      ]);
      if (lRes.ok) setHealthLatest(await lRes.json());
      if (hRes.ok) { const d = await hRes.json(); setHealthHistory(d.runs || []); }
    } catch { /* silent */ }
    setHealthLoading(false);
  }, [authHeaders]);

  /* ─── Faithfulness editor config ─── */
  const fetchEditorConfig = useCallback(async () => {
    setEditorLoading(true);
    try {
      const res = await fetch(`${API_BASE}/admin/editor-config`, { headers: authHeaders() });
      if (res.ok) {
        const d = await res.json();
        setEditorCfg(d);
        setEditorForm((f) => ({ ...f, provider: d.provider || 'off', model: d.model || 'claude-haiku-4-5' }));
      }
    } catch { /* silent */ }
    setEditorLoading(false);
  }, [authHeaders]);

  const saveEditorConfig = useCallback(async () => {
    setEditorSaving(true); setEditorMsg('');
    try {
      const body = { provider: editorForm.provider, model: editorForm.model };
      if (editorForm.access_token.trim()) body.access_token = editorForm.access_token.trim();
      if (editorForm.refresh_token.trim()) body.refresh_token = editorForm.refresh_token.trim();
      if (editorForm.expires_in) body.expires_in = Number(editorForm.expires_in);
      const res = await fetch(`${API_BASE}/admin/editor-config`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify(body),
      });
      const d = await res.json();
      if (res.ok) {
        setEditorMsg(d.active
          ? (d.verified === false ? '⚠️ Saved, but the Claude editor did not verify — check the tokens.' : '✅ Saved — Claude editor active and verified.')
          : '✅ Saved — using self-hosted editor.');
        setEditorForm((f) => ({ ...f, access_token: '', refresh_token: '' })); // clear secrets from the form
        fetchEditorConfig();
      } else {
        setEditorMsg(`❌ ${d.detail || 'Failed to save'}`);
      }
    } catch (e) { setEditorMsg(`❌ ${e.message}`); }
    setEditorSaving(false);
  }, [authHeaders, editorForm, fetchEditorConfig]);

  const startOauth = useCallback(async () => {
    setOauthBusy(true); setEditorMsg('');
    try {
      const res = await fetch(`${API_BASE}/admin/editor-config/oauth/start`, { method: 'POST', headers: authHeaders() });
      const d = await res.json();
      if (res.ok) { setOauthUrl(d.authorize_url); setOauthSession(d.session_id); }
      else setEditorMsg(`❌ ${d.detail || 'Could not start OAuth'}`);
    } catch (e) { setEditorMsg(`❌ ${e.message}`); }
    setOauthBusy(false);
  }, [authHeaders]);

  const finishOauth = useCallback(async () => {
    if (!oauthSession || !oauthCode.trim()) return;
    setOauthBusy(true); setEditorMsg('');
    try {
      const res = await fetch(`${API_BASE}/admin/editor-config/oauth/exchange`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ session_id: oauthSession, code: oauthCode.trim() }),
      });
      const d = await res.json();
      if (res.ok) {
        setEditorMsg(d.active
          ? (d.verified === false ? '⚠️ Token saved but the Claude editor did not verify — check the login.' : '✅ Token generated — Claude editor active and verified.')
          : '⚠️ Token exchanged but editor did not activate.');
        setOauthUrl(''); setOauthSession(''); setOauthCode('');
        setEditorForm((f) => ({ ...f, provider: 'anthropic' }));
        fetchEditorConfig();
      } else setEditorMsg(`❌ ${d.detail || 'Exchange failed'}`);
    } catch (e) { setEditorMsg(`❌ ${e.message}`); }
    setOauthBusy(false);
  }, [authHeaders, oauthSession, oauthCode, fetchEditorConfig]);

  /* ─── Fetch A/B threshold tuning results ─── */
  const fetchTuneHistory = useCallback(async () => {
    setTuneLoading(true);
    try {
      const res = await fetch(`${API_BASE}/admin/tune/history`, { headers: authHeaders() });
      if (res.ok) { const d = await res.json(); setTuneKnobs(d.knobs || {}); }
    } catch { /* silent */ }
    setTuneLoading(false);
  }, [authHeaders]);

  /* ─── Trigger an eval run (async on the server) ─── */
  const runEval = useCallback(async (mode = 'full') => {
    setEvalRunning(true); setError(''); setSuccess('');
    let prevRunAt = null;
    try {
      const cur = await fetch(`${API_BASE}/admin/eval/latest`, { headers: authHeaders() });
      if (cur.ok) { const cd = await cur.json(); prevRunAt = cd?.run_at || null; }
    } catch { /* ignore */ }
    try {
      const res = await fetch(`${API_BASE}/admin/eval/run?mode=${mode}`, {
        method: 'POST', headers: authHeaders(),
      });
      if (!res.ok) { setError('Failed to start eval (admin only).'); setEvalRunning(false); return; }
      setSuccess(`Eval running (${mode})… this takes a few minutes; results appear automatically.`);
      const startedAt = Date.now();
      const poll = async () => {
        try {
          const r = await fetch(`${API_BASE}/admin/eval/latest`, { headers: authHeaders() });
          if (r.ok) {
            const d = await r.json();
            if (d.has_run && d.run_at && d.run_at !== prevRunAt) {
              await fetchEval();
              setSuccess(`Eval complete — overall ${Math.round((d.overall_score || 0) * 100)}% (${d.passed_probes}/${d.total_probes} probes).`);
              setEvalRunning(false);
              return;
            }
          }
        } catch { /* ignore transient */ }
        if (Date.now() - startedAt > 12 * 60 * 1000) {
          setSuccess('Eval is taking longer than usual — hit Refresh in a bit to see the result.');
          setEvalRunning(false);
          return;
        }
        setTimeout(poll, 12000);
      };
      setTimeout(poll, 12000);
    } catch (e) { setError('Failed to start eval: ' + e.message); setEvalRunning(false); }
  }, [authHeaders, fetchEval]);

  /* ─── Run a health sweep now (server blocks until done, ~a few s) ─── */
  const runHealth = useCallback(async (tier = 'full') => {
    setHealthRunning(true); setError(''); setSuccess('');
    try {
      const res = await fetch(`${API_BASE}/admin/health/run?tier=${tier}`, {
        method: 'POST', headers: authHeaders(),
      });
      if (!res.ok) { setError('Failed to run health checks (admin only).'); setHealthRunning(false); return; }
      const snap = await res.json();
      setHealthLatest(snap);
      await fetchHealth();
      const d = snap.down_count || 0, g = snap.degraded_count || 0;
      setSuccess(d ? `${d} subsystem(s) DOWN, ${g} degraded.` : g ? `All up, ${g} degraded.` : 'All systems operational. ✅');
    } catch (e) { setError('Failed to run health checks: ' + e.message); }
    setHealthRunning(false);
  }, [authHeaders, fetchHealth]);

  /* ─── Fetch invites ─── */
  const fetchInvites = useCallback(async () => {
    setInvitesLoading(true);
    try {
      const res = await fetch(`${API_BASE}/admin/invites`, { headers: authHeaders() });
      if (res.ok) { const data = await res.json(); setInvites(data.invites || []); }
    } catch { /* silent */ }
    setInvitesLoading(false);
  }, [authHeaders]);

  /* ─── Create invite (code only) ─── */
  const handleCreateInvite = async (sendEmail = false) => {
    setInviteCreating(true); setError(''); setSuccess('');
    try {
      const endpoint = sendEmail ? '/admin/send-invite' : '/admin/invites';
      const body = { max_uses: inviteMaxUses };
      if (inviteEmail) body.email = inviteEmail;
      if (inviteExpiresDays) body.expires_days = parseInt(inviteExpiresDays);
      const res = await fetch(`${API_BASE}${endpoint}`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.success) {
        setSuccess(data.message || 'Invite created');
        setInviteEmail(''); setInviteMaxUses(1); setInviteExpiresDays('');
        setShowInviteForm(false);
        fetchInvites();
      } else { setError(data.detail || 'Failed to create invite'); }
    } catch (e) { setError(e.message); }
    setInviteCreating(false);
  };

  /* ─── Revoke invite ─── */
  const handleRevokeInvite = async (id) => {
    setError(''); setSuccess('');
    try {
      const res = await fetch(`${API_BASE}/admin/invites/${id}/revoke`, { method: 'POST', headers: authHeaders() });
      const data = await res.json();
      if (data.success) { setSuccess(data.message); fetchInvites(); }
      else setError(data.detail || 'Failed');
    } catch (e) { setError(e.message); }
  };

  /* ─── Delete invite ─── */
  const handleDeleteInvite = async (id) => {
    setError(''); setSuccess('');
    try {
      const res = await fetch(`${API_BASE}/admin/invites/${id}`, { method: 'DELETE', headers: authHeaders() });
      const data = await res.json();
      if (data.success) { setSuccess(data.message); fetchInvites(); }
      else setError(data.detail || 'Failed');
    } catch (e) { setError(e.message); }
  };

  /* ─── Fetch beta requests ─── */
  const fetchBetaRequests = useCallback(async () => {
    setBetaLoading(true);
    try {
      const res = await fetch(`${API_BASE}/admin/beta-requests`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setBetaRequests(data.requests || []);
        setBetaPending(data.pending_count || 0);
      }
    } catch { /* silent */ }
    setBetaLoading(false);
  }, [authHeaders]);

  /* ─── Approve beta request ─── */
  const handleApproveBeta = async (id) => {
    setBetaActing(id); setError(''); setSuccess('');
    try {
      const res = await fetch(`${API_BASE}/admin/beta-requests/${id}/approve`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({}),
      });
      const data = await res.json();
      if (data.success) {
        const code = data.invite && data.invite.code;
        setSuccess(data.message || (code ? `Approved — invite code ${code}` : 'Request approved'));
        fetchBetaRequests();
      } else setError(data.detail || 'Failed to approve request');
    } catch (e) { setError(e.message); }
    setBetaActing(null);
  };

  /* ─── Reject beta request ─── */
  const handleRejectBeta = async (id) => {
    setBetaActing(id); setError(''); setSuccess('');
    try {
      const res = await fetch(`${API_BASE}/admin/beta-requests/${id}/reject`, {
        method: 'POST', headers: authHeaders(),
      });
      const data = await res.json();
      if (data.success) { setSuccess(data.message || 'Request rejected'); fetchBetaRequests(); }
      else setError(data.detail || 'Failed to reject request');
    } catch (e) { setError(e.message); }
    setBetaActing(null);
  };

  /* ─── Delete beta request ─── */
  const handleDeleteBeta = async (id) => {
    setBetaActing(id); setError(''); setSuccess('');
    try {
      const res = await fetch(`${API_BASE}/admin/beta-requests/${id}`, { method: 'DELETE', headers: authHeaders() });
      const data = await res.json();
      if (data.success) { setSuccess(data.message || 'Request deleted'); fetchBetaRequests(); }
      else setError(data.detail || 'Failed to delete request');
    } catch (e) { setError(e.message); }
    setBetaActing(null);
  };

  /* ─── Send email (generic) ─── */
  const handleSendEmail = async () => {
    if (!emailComposeModal) return;
    const to = emailComposeModal.to;
    if (!to || !composeSubject || !composeMessage) return;
    setSendingEmail(true); setError(''); setSuccess('');
    try {
      const res = await fetch(`${API_BASE}/admin/send-email`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ to, subject: composeSubject, message: composeMessage }),
      });
      const data = await res.json();
      if (data.success) {
        setSuccess(data.message || `Email sent to ${to}`);
        setEmailComposeModal(null); setComposeSubject(''); setComposeMessage('');
      } else setError(data.detail || 'Failed to send email');
    } catch (e) { setError(e.message); }
    setSendingEmail(false);
  };

  /* ─── Fetch user detail ─── */
  const fetchUserDetail = async (uid) => {
    setDetailLoading(true);
    try {
      const res = await fetch(`${API_BASE}/admin/user/${uid}/detail`, { headers: authHeaders() });
      if (res.ok) setDetailData(await res.json());
    } catch { /* silent */ }
    setDetailLoading(false);
  };

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/'); return; }
    fetchUsers();
    fetchStats();
    fetchInvites();
    fetchBetaRequests();
    fetchEval();
    fetchHealth();
    fetchTuneHistory();
    fetchEditorConfig();
  }, [user, authLoading, navigate, fetchUsers, fetchStats, fetchInvites, fetchBetaRequests, fetchEval, fetchHealth, fetchTuneHistory, fetchEditorConfig]);

  /* ─── Actions ─── */
  const handleSetPassword = async () => {
    if (!pwModal || !newPassword) return;
    setSettingPw(true); setError(''); setSuccess('');
    try {
      const res = await fetch(`${API_BASE}/admin/set-user-password`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ uid: pwModal.uid, new_password: newPassword }),
      });
      const data = await res.json();
      if (data.success) { setSuccess(`Password set for ${pwModal.email}`); setPwModal(null); setNewPassword(''); fetchUsers(); }
      else setError(data.detail || 'Failed');
    } catch (e) { setError(e.message); }
    setSettingPw(false);
  };

  const handleUpdateEmail = async () => {
    if (!emailModal || !newEmail) return;
    setSettingEmail(true); setError(''); setSuccess('');
    try {
      const res = await fetch(`${API_BASE}/admin/update-user`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ uid: emailModal.uid, new_email: newEmail }),
      });
      const data = await res.json();
      if (data.success) { setSuccess(`Email updated → ${newEmail}`); setEmailModal(null); setNewEmail(''); fetchUsers(); }
      else setError(data.detail || 'Failed');
    } catch (e) { setError(e.message); }
    setSettingEmail(false);
  };

  const handleToggleAdmin = async (u) => {
    setTogglingAdmin(u.uid); setError(''); setSuccess('');
    try {
      const res = await fetch(`${API_BASE}/admin/update-user`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ uid: u.uid, is_admin: !u.is_admin }),
      });
      const data = await res.json();
      if (data.success) { setSuccess(`${u.email}: admin ${!u.is_admin ? 'granted' : 'revoked'}`); fetchUsers(); }
      else setError(data.detail || 'Failed');
    } catch (e) { setError(e.message); }
    setTogglingAdmin(null);
  };

  /* ─── Compact "time ago" for last-login / last-session cells ─── */
  const fmtAgo = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (isNaN(d)) return '—';
    const secs = (Date.now() - d.getTime()) / 1000;
    if (secs < 60) return 'just now';
    const mins = secs / 60;
    if (mins < 60) return `${Math.floor(mins)}m ago`;
    const hrs = mins / 60;
    if (hrs < 24) return `${Math.floor(hrs)}h ago`;
    const days = hrs / 24;
    if (days < 7) return `${Math.floor(days)}d ago`;
    if (days < 30) return `${Math.floor(days / 7)}w ago`;
    return d.toLocaleDateString();
  };

  /* ─── Filter & sort users ─── */
  const filtered = users
    .filter(u => {
      const q = search.toLowerCase();
      return (u.email || '').toLowerCase().includes(q) || (u.display_name || '').toLowerCase().includes(q) || (u.uid || '').toLowerCase().includes(q);
    })
    .sort((a, b) => {
      let va = a[sortField] || '', vb = b[sortField] || '';
      if (sortField === 'created_at' || sortField === 'last_login_at' || sortField === 'last_session_at') {
        va = new Date(va || 0); vb = new Date(vb || 0);
      }
      if (typeof va === 'string') va = va.toLowerCase();
      if (typeof vb === 'string') vb = vb.toLowerCase();
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const sortIcon = (field) => sortField === field ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

  /* ─── Stats helpers ─── */
  const s = stats || {};
  const statCards = [
    { label: 'Total Users', value: s.total_users ?? '—', sub: `+${s.new_users_24h ?? 0} today · +${s.new_users_7d ?? 0} this week`, icon: '👥', tone: 'accent' },
    { label: 'Total Messages', value: s.total_messages ?? '—', sub: `${s.messages_24h ?? 0} in last 24h`, icon: '💬' },
    { label: 'Total Memories', value: s.total_memories ?? '—', sub: `${s.geo_memories_count ?? 0} geo-memories`, icon: '🧠' },
    { label: 'Active Sessions', value: s.active_sessions_24h ?? '—', sub: `${s.total_sessions ?? 0} total sessions`, icon: '⚡' },
    { label: 'Successful Calls', value: s.successful_calls ?? '—', sub: `${s.successful_calls_7d ?? 0} this week · ${s.calls_total ?? 0} attempted`, icon: '📞', tone: 'accent' },
    { label: 'Safety Flags', value: s.safety_flags ?? '—', sub: 'medium/high concerns in calls', icon: '🚩', tone: (s.safety_flags > 0 ? 'crit' : undefined) },
    { label: 'Admins', value: s.admin_count ?? '—', sub: '', icon: '🛡️' },
    { label: 'Share Links', value: s.share_links_count ?? '—', sub: '', icon: '🔗' },
  ];

  // Plain-language definitions shown on hover in the AI Quality → By dimension list.
  const DIMENSION_DEFS = {
    recall: 'Surfaces the right saved memories when the user asks about them.',
    safety: 'Handles crisis or risk language safely — escalates, never encourages harm.',
    warmth: 'Responds with genuine warmth and care, never cold or clinical.',
    abstention: "Says 'I don't have that' instead of inventing when no memory covers it.",
    attribution: 'Keeps every fact attached to the correct person — no mixing people up.',
    consistency: 'Gives the same answer to the same question across turns.',
    dignity_tone: 'Speaks to the user with dignity — never condescending or infantilizing.',
    faithfulness: "States only what's actually in the user's memories; no fabrication.",
    grounding: "Answers strictly from the retrieved memories, not made-up detail.",
    relationship_speculation: 'Never invents the state of a relationship (tension, distance) unless a memory says so.',
  };

  /* ═══════════════════════ RENDER ════════════════════════════════ */
  return (
    <div className="ad-root">
      {/* ─── Sidebar ─── */}
      <aside className="ad-sidebar">
        <div className="ad-sidebar-logo" onClick={() => navigate('/app')}>
          <img src="https://ssobjects.blob.core.windows.net/logo/logo.png" alt="Logo" className="ad-logo-img" />
          <span className="ad-logo-text">Admin</span>
        </div>
        <nav className="ad-nav">
          {[
            { id: 'dashboard', label: 'Dashboard', icon: '📊' },
            { id: 'users', label: 'Users', icon: '👥' },
            { id: 'quality', label: 'AI Quality', icon: '🧪' },
            { id: 'health', label: 'System Health', icon: '🩺' },
            { id: 'tuning', label: 'A/B Tuning', icon: '🎛️' },
            { id: 'editor', label: 'Faithfulness Editor', icon: '✂️' },
            { id: 'beta', label: 'Beta Requests', icon: '🚀' },
            { id: 'invites', label: 'Invites', icon: '🎟️' },
            { id: 'system', label: 'System', icon: '⚙️' },
          ].map(n => (
            <button
              key={n.id}
              className={`ad-nav-item ${view === n.id ? 'ad-nav-active' : ''}`}
              onClick={() => { setView(n.id); setDetailUser(null); if (n.id === 'beta') fetchBetaRequests(); }}
            >
              <span className="ad-nav-icon">{n.icon}</span>
              <span className="ad-nav-label">{n.label}</span>
              {n.id === 'beta' && betaPending > 0 && <span className="ad-nav-badge">{betaPending}</span>}
            </button>
          ))}
        </nav>
        <div className="ad-sidebar-footer">
          <button className="ad-nav-item" onClick={() => navigate('/app')}>
            <span className="ad-nav-icon">←</span>
            <span className="ad-nav-label">Back to App</span>
          </button>
        </div>
      </aside>

      {/* ─── Main Content ─── */}
      <main className="ad-main">
        {error && <div className="ad-alert ad-alert-error">{error} <button className="ad-alert-dismiss" onClick={() => setError('')}>×</button></div>}
        {success && <div className="ad-alert ad-alert-success">{success} <button className="ad-alert-dismiss" onClick={() => setSuccess('')}>×</button></div>}

        {/* ═══ DASHBOARD VIEW ═══ */}
        {view === 'dashboard' && (
          <div className="ad-view">
            <div className="ad-view-header">
              <div>
                <h1>Dashboard</h1>
                <div className="ad-view-sub">os-dev.heartfeltsouls.com{statsLoading ? ' · updating…' : ' · updated just now'}</div>
              </div>
              <div className="ad-head-actions">
                <button className="ad-refresh-btn" onClick={() => { fetchStats(); fetchUsers(); fetchEval(); }} disabled={statsLoading}>
                  {statsLoading ? 'Refreshing…' : '⟳ Refresh'}
                </button>
                <button className="ad-refresh-btn ad-btn-primary" onClick={() => runEval('full')} disabled={evalRunning}>
                  {evalRunning ? '⏳ Running…' : 'Run eval'}
                </button>
              </div>
            </div>

            {/* System-health status strip — first thing you see; click through for detail */}
            {healthLatest && healthLatest.has_run && (
              <div className={`ad-health-strip ad-hb-${healthLatest.overall_status}`}
                onClick={() => setView('health')} role="button" tabIndex={0}>
                <span className="ad-hb-dot" />
                <span className="ad-hs-text">
                  {healthLatest.overall_status === 'ok'
                    ? 'All systems operational'
                    : healthLatest.overall_status === 'down'
                      ? `${healthLatest.down_count} subsystem(s) DOWN`
                      : `${healthLatest.degraded_count} subsystem(s) degraded`}
                </span>
                {(healthLatest.checks || []).filter(c => c.status === 'down' || c.status === 'degraded').slice(0, 4).map(c => (
                  <span key={c.name} className={`ad-hs-chip ad-hc-${c.status}`}>{c.name.replace(/_/g, ' ')}</span>
                ))}
                <span className="ad-hs-more">System Health →</span>
              </div>
            )}

            {/* 4 hero KPIs (matches the design) */}
            <div className="ad-stat-grid ad-kpi-4">
              {[
                { label: 'Total Users', value: s.total_users ?? '—', sub: `+${s.new_users_7d ?? 0} this week`, icon: '👥', tone: 'accent' },
                { label: 'Memories Stored', value: s.total_memories ?? '—', sub: `${s.geo_memories_count ?? 0} geo-tagged`, icon: '🧠', tone: 'accent' },
                { label: 'Successful Calls', value: s.successful_calls ?? '—', sub: `${s.successful_calls_7d ?? 0} this week · ${s.calls_total ?? 0} attempted`, icon: '📞', tone: 'accent' },
                { label: 'Safety Flags', value: s.safety_flags ?? '—', sub: (s.safety_flags > 0 ? 'needs review' : 'all clear'), icon: '🚩', tone: (s.safety_flags > 0 ? 'crit' : undefined) },
                { label: 'AI Quality', value: (evalLatest && evalLatest.has_run) ? Math.round((evalLatest.overall_score || 0) * 100) + '%' : '—', sub: (evalLatest && evalLatest.has_run) ? `${evalLatest.passed_probes}/${evalLatest.total_probes} probes` : 'no run yet', icon: '🧪', tone: 'accent' },
              ].map(c => (
                <div key={c.label} className={`ad-stat-card${c.tone ? ' ad-stat-' + c.tone : ''}`}>
                  <div className="ad-stat-top">
                    <div className="ad-stat-label">{c.label}</div>
                    <div className="ad-stat-icon">{c.icon}</div>
                  </div>
                  <div className="ad-stat-value">{typeof c.value === 'number' ? c.value.toLocaleString() : c.value}</div>
                  {c.sub && <div className="ad-stat-sub">{c.sub}</div>}
                </div>
              ))}
            </div>

            {/* Safety alert — surfaced high, only when there are flags to review */}
            {(s.recent_safety_flags && s.recent_safety_flags.length > 0) && (
              <div className="ad-card" style={{ marginBottom: 20, boxShadow: 'inset 3px 0 0 var(--crit)' }}>
                <h3 style={{ marginTop: 0 }}>🚩 Safety flags to review ({s.safety_flags ?? s.recent_safety_flags.length})</h3>
                {s.recent_safety_flags.map((f, i) => (
                  <div key={i} className="ad-fail" style={{ display: 'flex', gap: 10, alignItems: 'baseline' }}>
                    <span className="ad-badge" style={{ background: f.severity === 'high' ? 'rgba(224,90,82,.18)' : 'rgba(217,154,43,.16)', color: f.severity === 'high' ? '#ffb3ad' : '#e8c98a' }}>{f.severity}</span>
                    <div style={{ flex: 1 }}>
                      <div className="ad-fail-q" style={{ fontWeight: 400 }}>{f.text}</div>
                      <div className="ad-fail-why">
                        {f.uid ? `user ${String(f.uid).slice(0, 8)}` : ''}
                        {f.analyzed_at ? ` · ${new Date(f.analyzed_at).toLocaleDateString()}` : ''}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* AI Quality + Recent sign-ins (signature row) */}
            <div className="ad-grid-2">
              <div className="ad-card">
                <div className="ad-card-h">
                  <span className="ad-card-t">AI Quality</span>
                  <span className="ad-card-note">{evalHistory.length > 1 ? `last ${evalHistory.length} runs` : 'nightly eval'}</span>
                </div>
                {evalLatest && evalLatest.has_run ? (
                  <>
                    <div className="ad-q-score">
                      <span className="ad-q-big">{Math.round((evalLatest.overall_score || 0) * 100)}%</span>
                      <span className="ad-q-of">overall · {evalLatest.passed_probes}/{evalLatest.total_probes} probes passed</span>
                    </div>
                    {(() => {
                      const sp = sparkPaths(evalHistory.slice().reverse().map(r => r.overall_score || 0));
                      return sp ? (
                        <svg className="ad-spark" viewBox={`0 0 ${sp.w} ${sp.h}`} preserveAspectRatio="none" role="img" aria-label="AI quality trend">
                          <defs>
                            <linearGradient id="adSparkFill" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0" stopColor="#5fae91" stopOpacity="0.30" />
                              <stop offset="1" stopColor="#5fae91" stopOpacity="0" />
                            </linearGradient>
                          </defs>
                          <path d={sp.area} fill="url(#adSparkFill)" />
                          <path d={sp.line} fill="none" stroke="#5fae91" strokeWidth="2"
                            strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
                          <circle cx={sp.last[0]} cy={sp.last[1]} r="3" fill="#8fe0c2" />
                        </svg>
                      ) : null;
                    })()}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 9, marginTop: 15 }}>
                      {Object.entries(evalLatest.dimension_scores || {}).slice(0, 4).map(([dim, sc]) => {
                        const pct = Math.round((typeof sc === 'object' ? (sc.score ?? sc.pass_rate ?? 0) : sc) * 100);
                        const cls = pct >= 90 ? '' : pct >= 70 ? ' warn' : ' crit';
                        return (
                          <div key={dim} className="ad-dim-row">
                            <span className="ad-dim-name" data-tip={DIMENSION_DEFS[dim] || `Score for ${dim.replace(/_/g, ' ')}.`} aria-label={DIMENSION_DEFS[dim] || ''} tabIndex={0} style={{ width: 118 }}>{dim.replace(/_/g, ' ')}<span className="ad-info">i</span></span>
                            <span className="ad-dim-track"><span className={`ad-dim-fill${cls}`} style={{ width: `${pct}%` }} /></span>
                            <span className="ad-dim-pct">{pct}%</span>
                          </div>
                        );
                      })}
                    </div>
                    <button className="ad-refresh-btn" style={{ marginTop: 15 }} onClick={() => setView('quality')}>View all dimensions →</button>
                  </>
                ) : (
                  <div className="ad-empty">No eval yet — <button className="ad-btn-sm ad-btn-primary" onClick={() => setView('quality')}>Open AI Quality</button></div>
                )}
              </div>

              <div className="ad-card">
                <div className="ad-card-h"><span className="ad-card-t">Recent sign-ins</span><span className="ad-card-note">live</span></div>
                <div className="ad-feed">
                  {users.slice(0, 6).map(u => (
                    <div key={u.uid} className="ad-feed-row" onClick={() => { setView('users'); setDetailUser(u); fetchUserDetail(u.uid); }}>
                      <div className="ad-recent-avatar" style={{ width: 32, height: 32, fontSize: 13 }}>{(u.email || '?')[0].toUpperCase()}</div>
                      <div>
                        <div className="ad-recent-name">{u.display_name || u.email}</div>
                        <div className="ad-recent-date">{u.is_admin ? 'Admin · ' : ''}{u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}</div>
                      </div>
                      {u.is_admin && <span className="ad-badge ad-badge-admin">Admin</span>}
                    </div>
                  ))}
                  {users.length === 0 && <div className="ad-empty">No users yet</div>}
                </div>
              </div>
            </div>

            {/* Users table (matches the design) */}
            <div className="ad-tbl-wrap" style={{ marginBottom: 20 }}>
              <table className="ad-table">
                <thead>
                  <tr><th>User</th><th>Role</th><th>Joined</th><th>Last login</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {users.slice(0, 8).map(u => (
                    <tr key={u.uid} onClick={() => { setView('users'); setDetailUser(u); fetchUserDetail(u.uid); }}>
                      <td><span style={{ color: 'var(--text)', fontWeight: 500 }}>{u.display_name || u.email}</span></td>
                      <td>{u.is_admin ? <span className="ad-badge ad-badge-admin">Admin</span> : 'Member'}</td>
                      <td className="ad-td-date">{u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}</td>
                      <td className="ad-td-date">{u.last_login_at ? new Date(u.last_login_at).toLocaleDateString() : '—'}</td>
                      <td><span className="ad-badge ad-badge-ok">Active</span></td>
                    </tr>
                  ))}
                  {users.length === 0 && <tr><td colSpan={5} className="ad-empty-row">No users yet</td></tr>}
                </tbody>
              </table>
            </div>

          </div>
        )}

        {/* ═══ USERS VIEW ═══ */}
        {view === 'users' && (
          <div className="ad-view ad-view-users">
            <div className="ad-view-header">
              <h1>Users <span className="ad-header-count">{users.length}</span></h1>
              <button className="ad-refresh-btn" onClick={fetchUsers} disabled={loading}>
                {loading ? 'Loading…' : 'Refresh'}
              </button>
            </div>

            <div className="ad-toolbar">
              <input
                className="ad-search"
                type="text"
                placeholder="Search by email, name, or UID…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>

            <div className="ad-users-layout">
              <div className={`ad-table-wrap ${detailUser ? 'ad-table-narrow' : ''}`}>
                <table className="ad-table">
                  <thead>
                    <tr>
                      <th className="ad-th-sortable" onClick={() => toggleSort('email')}>Email{sortIcon('email')}</th>
                      <th className="ad-th-sortable" onClick={() => toggleSort('display_name')}>Name{sortIcon('display_name')}</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th className="ad-th-sortable" onClick={() => toggleSort('created_at')}>Joined{sortIcon('created_at')}</th>
                      <th className="ad-th-sortable" onClick={() => toggleSort('last_login_at')}>Last login{sortIcon('last_login_at')}</th>
                      <th className="ad-th-sortable" onClick={() => toggleSort('last_session_at')}>Last session{sortIcon('last_session_at')}</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(u => (
                      <tr
                        key={u.uid}
                        className={`${u.needs_password_reset ? 'ad-row-warn' : ''} ${detailUser?.uid === u.uid ? 'ad-row-selected' : ''}`}
                        onClick={() => { setDetailUser(u); fetchUserDetail(u.uid); }}
                      >
                        <td className="ad-td-email">{u.email}</td>
                        <td>{u.display_name || <span className="ad-muted">—</span>}</td>
                        <td>{u.is_admin ? <span className="ad-badge ad-badge-admin">Admin</span> : <span className="ad-badge">User</span>}</td>
                        <td>{u.needs_password_reset ? <span className="ad-badge ad-badge-warn">PW Reset</span> : <span className="ad-badge ad-badge-ok">Active</span>}</td>
                        <td className="ad-td-date">{u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}</td>
                        <td className="ad-td-date" title={u.last_login_at ? new Date(u.last_login_at).toLocaleString() : ''}>{fmtAgo(u.last_login_at)}</td>
                        <td className="ad-td-date" title={u.last_session_at ? new Date(u.last_session_at).toLocaleString() : ''}>{fmtAgo(u.last_session_at)}</td>
                        <td className="ad-td-actions" onClick={e => e.stopPropagation()}>
                          <button className="ad-btn-sm" onClick={() => { setPwModal({ uid: u.uid, email: u.email }); setNewPassword(''); }}>PW</button>
                          <button className="ad-btn-sm" onClick={() => { setEmailModal({ uid: u.uid, email: u.email }); setNewEmail(u.email || ''); }}>Email</button>
                          <button
                            className={`ad-btn-sm ${u.is_admin ? 'ad-btn-danger' : 'ad-btn-success'}`}
                            onClick={() => handleToggleAdmin(u)}
                            disabled={togglingAdmin === u.uid}
                          >
                            {togglingAdmin === u.uid ? '…' : u.is_admin ? 'Revoke' : 'Grant'}
                          </button>
                        </td>
                      </tr>
                    ))}
                    {filtered.length === 0 && (
                      <tr><td colSpan="8" className="ad-empty-row">No users found</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* User detail drawer */}
              {detailUser && (
                <div className="ad-detail-drawer">
                  <div className="ad-detail-header">
                    <h3>User Detail</h3>
                    <button className="ad-detail-close" onClick={() => { setDetailUser(null); setDetailData(null); }}>×</button>
                  </div>
                  {detailLoading ? (
                    <div className="ad-detail-loading">Loading…</div>
                  ) : detailData ? (
                    <div className="ad-detail-body">
                      <div className="ad-detail-avatar">{(detailData.email || '?')[0].toUpperCase()}</div>
                      <div className="ad-detail-name">{detailData.display_name || detailData.email}</div>
                      <div className="ad-detail-email">{detailData.email}</div>
                      <div className="ad-detail-uid">UID: {detailData.uid}</div>

                      <div className="ad-detail-stats">
                        <div className="ad-detail-stat">
                          <div className="ad-detail-stat-val">{detailData.messages}</div>
                          <div className="ad-detail-stat-label">Messages</div>
                        </div>
                        <div className="ad-detail-stat">
                          <div className="ad-detail-stat-val">{detailData.memories}</div>
                          <div className="ad-detail-stat-label">Memories</div>
                        </div>
                        <div className="ad-detail-stat">
                          <div className="ad-detail-stat-val">{detailData.sessions}</div>
                          <div className="ad-detail-stat-label">Sessions</div>
                        </div>
                      </div>

                      <div className="ad-detail-meta">
                        <div className="ad-detail-row"><span>Joined</span><span>{detailData.created_at ? new Date(detailData.created_at).toLocaleString() : '—'}</span></div>
                        <div className="ad-detail-row"><span>Last login</span><span>{detailUser?.last_login_at ? new Date(detailUser.last_login_at).toLocaleString() : '—'}</span></div>
                        <div className="ad-detail-row"><span>Last session</span><span>{detailUser?.last_session_at ? new Date(detailUser.last_session_at).toLocaleString() : '—'}</span></div>
                        <div className="ad-detail-row"><span>Last Active</span><span>{detailData.last_activity ? new Date(detailData.last_activity * 1000).toLocaleString() : '—'}</span></div>
                        <div className="ad-detail-row"><span>Admin</span><span>{detailData.is_admin ? 'Yes' : 'No'}</span></div>
                        <div className="ad-detail-row"><span>Onboarded</span><span>{detailData.onboarding_complete ? 'Yes' : 'No'}</span></div>
                        <div className="ad-detail-row"><span>Memory</span><span>{detailData.memory_enabled ? 'Enabled' : 'Disabled'}</span></div>
                        <div className="ad-detail-row"><span>Model</span><span>{detailData.selected_model || '—'}</span></div>
                      </div>

                      <div className="ad-detail-actions">
                        <button className="ad-btn-sm" onClick={() => { setPwModal({ uid: detailData.uid, email: detailData.email }); setNewPassword(''); }}>Set Password</button>
                        <button className="ad-btn-sm" onClick={() => { setEmailModal({ uid: detailData.uid, email: detailData.email }); setNewEmail(detailData.email || ''); }}>Edit Email</button>
                        <button
                          className={`ad-btn-sm ${detailData.is_admin ? 'ad-btn-danger' : 'ad-btn-success'}`}
                          onClick={() => { handleToggleAdmin({ uid: detailData.uid, email: detailData.email, is_admin: detailData.is_admin }); }}
                        >
                          {detailData.is_admin ? 'Revoke Admin' : 'Grant Admin'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="ad-detail-loading">No data</div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ═══ BETA REQUESTS VIEW ═══ */}
        {view === 'beta' && (
          <div className="ad-view">
            <div className="ad-view-header">
              <h1>
                Beta Requests <span className="ad-header-count">{betaRequests.length}</span>
                {betaPending > 0 && <span className="ad-badge ad-badge-warn" style={{ marginLeft: 10 }}>{betaPending} pending</span>}
              </h1>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  className="ad-refresh-btn"
                  onClick={() => { setEmailComposeModal({ to: '' }); setComposeSubject(''); setComposeMessage(''); }}
                >
                  ✉️ Compose Email
                </button>
                <button className="ad-refresh-btn" onClick={fetchBetaRequests} disabled={betaLoading}>
                  {betaLoading ? 'Loading…' : 'Refresh'}
                </button>
              </div>
            </div>

            <div className="ad-table-wrap">
              <table className="ad-table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Name</th>
                    <th>Message</th>
                    <th>Status</th>
                    <th>Invite Code</th>
                    <th>Requested</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {betaRequests.map(r => (
                    <tr key={r.id}>
                      <td className="ad-td-email">{r.email}</td>
                      <td>{r.name || <span className="ad-muted">—</span>}</td>
                      <td className="ad-td-message" title={r.message || ''}>{r.message || <span className="ad-muted">—</span>}</td>
                      <td>
                        {r.status === 'pending' && <span className="ad-badge ad-badge-warn">Pending</span>}
                        {r.status === 'approved' && <span className="ad-badge ad-badge-ok">Approved</span>}
                        {r.status === 'rejected' && <span className="ad-badge">Rejected</span>}
                      </td>
                      <td>
                        {r.invite_code
                          ? <span style={{ fontFamily: 'monospace', fontWeight: 600, letterSpacing: '1px', color: '#e8c77b' }}>{r.invite_code}</span>
                          : <span className="ad-muted">—</span>}
                      </td>
                      <td className="ad-td-date">{r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}</td>
                      <td className="ad-td-actions">
                        {r.status === 'pending' && (
                          <>
                            <button
                              className="ad-btn-sm ad-btn-success"
                              onClick={() => handleApproveBeta(r.id)}
                              disabled={betaActing === r.id}
                            >
                              {betaActing === r.id ? '…' : 'Approve'}
                            </button>
                            <button
                              className="ad-btn-sm ad-btn-danger"
                              onClick={() => handleRejectBeta(r.id)}
                              disabled={betaActing === r.id}
                            >
                              Reject
                            </button>
                          </>
                        )}
                        <button
                          className="ad-btn-sm"
                          onClick={() => { setEmailComposeModal({ to: r.email }); setComposeSubject(''); setComposeMessage(''); }}
                        >
                          Email
                        </button>
                        <button
                          className="ad-btn-sm ad-btn-danger"
                          onClick={() => handleDeleteBeta(r.id)}
                          disabled={betaActing === r.id}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                  {betaRequests.length === 0 && (
                    <tr><td colSpan="7" className="ad-empty-row">{betaLoading ? 'Loading…' : 'No beta requests yet'}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ═══ INVITES VIEW ═══ */}
        {view === 'invites' && (
          <div className="ad-view">
            <div className="ad-view-header">
              <h1>Invite Codes <span className="ad-header-count">{invites.length}</span></h1>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="ad-refresh-btn" onClick={() => setShowInviteForm(!showInviteForm)}>
                  {showInviteForm ? 'Cancel' : '+ New Invite'}
                </button>
                <button className="ad-refresh-btn" onClick={fetchInvites} disabled={invitesLoading}>
                  {invitesLoading ? 'Loading…' : 'Refresh'}
                </button>
              </div>
            </div>

            {showInviteForm && (
              <div className="ad-section" style={{ marginBottom: 20 }}>
                <div className="ad-section-title">Create Invite</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 400 }}>
                  <input
                    className="ad-search"
                    type="email"
                    placeholder="Email address (optional — locks code to this email)"
                    value={inviteEmail}
                    onChange={e => setInviteEmail(e.target.value)}
                  />
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <label style={{ fontSize: 13, color: '#aaa', minWidth: 80 }}>Max uses:</label>
                    <input
                      className="ad-search"
                      type="number" min="1" max="100"
                      value={inviteMaxUses}
                      onChange={e => setInviteMaxUses(parseInt(e.target.value) || 1)}
                      style={{ width: 80 }}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <label style={{ fontSize: 13, color: '#aaa', minWidth: 80 }}>Expires in:</label>
                    <input
                      className="ad-search"
                      type="number" min="1" max="365"
                      placeholder="days (empty = never)"
                      value={inviteExpiresDays}
                      onChange={e => setInviteExpiresDays(e.target.value)}
                      style={{ width: 140 }}
                    />
                    <span style={{ fontSize: 12, color: '#666' }}>days</span>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      className="ad-btn-sm ad-btn-success"
                      onClick={() => handleCreateInvite(false)}
                      disabled={inviteCreating}
                      style={{ padding: '6px 14px' }}
                    >
                      {inviteCreating ? 'Creating…' : 'Create Code'}
                    </button>
                    {inviteEmail && (
                      <button
                        className="ad-btn-sm ad-btn-primary"
                        onClick={() => handleCreateInvite(true)}
                        disabled={inviteCreating}
                        style={{ padding: '6px 14px' }}
                      >
                        {inviteCreating ? 'Sending…' : 'Create & Send Email'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="ad-table-wrap">
              <table className="ad-table">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Email</th>
                    <th>Status</th>
                    <th>Uses</th>
                    <th>Created By</th>
                    <th>Used By</th>
                    <th>Expires</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invites.map(inv => (
                    <tr key={inv.id}>
                      <td style={{ fontFamily: 'monospace', fontWeight: 600, letterSpacing: '1px', color: '#e8c77b' }}>{inv.code}</td>
                      <td className="ad-td-email">{inv.email || <span className="ad-muted">—</span>}</td>
                      <td>
                        {inv.status === 'active' && <span className="ad-badge ad-badge-ok">Active</span>}
                        {inv.status === 'used' && <span className="ad-badge">Used</span>}
                        {inv.status === 'revoked' && <span className="ad-badge ad-badge-warn">Revoked</span>}
                      </td>
                      <td>{inv.use_count} / {inv.max_uses}</td>
                      <td className="ad-td-email">{inv.creator_email || <span className="ad-muted">—</span>}</td>
                      <td className="ad-td-email">{inv.used_by_email || <span className="ad-muted">—</span>}</td>
                      <td className="ad-td-date">{inv.expires_at ? new Date(inv.expires_at).toLocaleDateString() : 'Never'}</td>
                      <td className="ad-td-date">{inv.created_at ? new Date(inv.created_at).toLocaleDateString() : '—'}</td>
                      <td className="ad-td-actions">
                        {inv.status === 'active' && (
                          <button className="ad-btn-sm ad-btn-danger" onClick={() => handleRevokeInvite(inv.id)}>Revoke</button>
                        )}
                        <button className="ad-btn-sm" onClick={() => handleDeleteInvite(inv.id)}>Delete</button>
                        <button className="ad-btn-sm" onClick={() => { navigator.clipboard.writeText(inv.code); setSuccess(`Copied ${inv.code}`); }}>Copy</button>
                      </td>
                    </tr>
                  ))}
                  {invites.length === 0 && (
                    <tr><td colSpan="9" className="ad-empty-row">No invite codes yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ═══ AI QUALITY VIEW ═══ */}
        {view === 'quality' && (
          <div className="ad-view">
            <div className="ad-view-header">
              <h1>AI Quality</h1>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="ad-refresh-btn" onClick={fetchEval} disabled={evalLoading}>
                  {evalLoading ? 'Loading…' : 'Refresh'}
                </button>
                <button className="ad-refresh-btn" onClick={() => runEval('full')} disabled={evalRunning}>
                  {evalRunning ? '⏳ Running…' : '▶ Run now'}
                </button>
              </div>
            </div>

            {(!evalLatest || !evalLatest.has_run) ? (
              <div className="ad-empty">No eval has run yet. Click “Run now,” or wait for the nightly run.</div>
            ) : (
              <>
                {/* Overall + meta */}
                <div className="ad-stat-grid" style={{ marginBottom: 16 }}>
                  <div className="ad-stat-card">
                    <div className="ad-stat-value">{Math.round((evalLatest.overall_score || 0) * 100)}%</div>
                    <div className="ad-stat-label">Overall quality</div>
                  </div>
                  <div className="ad-stat-card">
                    <div className="ad-stat-value">{evalLatest.passed_probes}/{evalLatest.total_probes}</div>
                    <div className="ad-stat-label">Probes passed</div>
                  </div>
                  <div className="ad-stat-card">
                    <div className="ad-stat-value" style={{ fontSize: 14 }}>{(evalLatest.git_commit || '—')}</div>
                    <div className="ad-stat-label">Commit tested</div>
                  </div>
                  <div className="ad-stat-card">
                    <div className="ad-stat-value" style={{ fontSize: 13 }}>
                      {evalLatest.run_at ? new Date(evalLatest.run_at).toLocaleString() : '—'}
                    </div>
                    <div className="ad-stat-label">Last run</div>
                  </div>
                </div>

                {/* Per-dimension scores — hover a name for what it measures */}
                <div className="ad-card">
                  <h3>By dimension <span style={{ fontWeight: 400, color: 'var(--faint)', fontSize: 12 }}>· hover a name for its definition</span></h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 11, marginTop: 8 }}>
                    {Object.entries(evalLatest.dimension_scores || {}).map(([dim, sc]) => {
                      const pct = Math.round((typeof sc === 'object' ? (sc.score ?? sc.pass_rate ?? 0) : sc) * 100);
                      const cls = pct >= 90 ? '' : pct >= 70 ? ' warn' : ' crit';
                      const def = DIMENSION_DEFS[dim] || `Score for ${dim.replace(/_/g, ' ')}.`;
                      return (
                        <div key={dim} className="ad-dim-row">
                          <span className="ad-dim-name" data-tip={def} aria-label={def} tabIndex={0}>
                            {dim.replace(/_/g, ' ')}<span className="ad-info">i</span>
                          </span>
                          <span className="ad-dim-track"><span className={`ad-dim-fill${cls}`} style={{ width: `${pct}%` }} /></span>
                          <span className="ad-dim-pct">{pct}%</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Trend */}
                {evalHistory.length > 1 && (
                  <div className="ad-card" style={{ marginTop: 16 }}>
                    <h3>Trend (recent runs)</h3>
                    <div className="ad-trend">
                      {evalHistory.slice().reverse().map((r) => {
                        const pct = Math.round((r.overall_score || 0) * 100);
                        const cls = pct >= 90 ? '' : pct >= 70 ? 'warn' : 'crit';
                        return (
                          <i key={r.id} className={cls} style={{ height: `${Math.max(pct, 3)}%` }}
                            title={`${pct}% — ${r.git_commit || ''} — ${r.run_at ? new Date(r.run_at).toLocaleDateString() : ''}`} />
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Failing probes */}
                <div className="ad-card" style={{ marginTop: 16 }}>
                  <h3>Failing probes ({(evalLatest.failures || []).length})</h3>
                  {(evalLatest.failures || []).length === 0 ? (
                    <div className="ad-empty">Nothing failing — clean run. 🎉</div>
                  ) : (
                    (evalLatest.failures || []).map((f, i) => (
                      <div key={i} className="ad-fail">
                        <div className="ad-fail-dim">{f.dimension || 'probe'}</div>
                        <div className="ad-fail-q">Q: {f.message || f.question}</div>
                        <div className="ad-fail-a">A: {f.answer || f.response}</div>
                        {f.reason && <div className="ad-fail-why">↳ {f.reason}</div>}
                      </div>
                    ))
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* ═══ SYSTEM HEALTH VIEW ═══ */}
        {view === 'health' && (
          <div className="ad-view">
            <div className="ad-view-header">
              <h1>System Health</h1>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="ad-refresh-btn" onClick={fetchHealth} disabled={healthLoading}>
                  {healthLoading ? 'Loading…' : 'Refresh'}
                </button>
                <button className="ad-refresh-btn" onClick={() => runHealth('full')} disabled={healthRunning}>
                  {healthRunning ? '⏳ Checking…' : '▶ Run checks now'}
                </button>
              </div>
            </div>

            {(!healthLatest || !healthLatest.has_run) ? (
              <div className="ad-empty">No health sweep yet — click “Run checks now,” or wait for the first automatic sweep (~45s after a restart).</div>
            ) : (
              <>
                {/* Overall banner */}
                {(() => {
                  const st = healthLatest.overall_status;
                  const meta = {
                    ok: ['ok', 'All systems operational'],
                    degraded: ['degraded', 'Running with degraded subsystems'],
                    down: ['down', 'One or more subsystems are DOWN'],
                  }[st] || ['unknown', 'No data yet'];
                  const lr = healthLatest.last_run_at || {};
                  const when = lr.full || lr.light || lr.heavy;
                  return (
                    <div className={`ad-health-banner ad-hb-${meta[0]}`}>
                      <span className="ad-hb-dot" />
                      <div>
                        <div className="ad-hb-title">{meta[1]}</div>
                        <div className="ad-hb-sub">
                          {healthLatest.ok_count} ok · {healthLatest.degraded_count} degraded · {healthLatest.down_count} down
                          {when ? ` · checked ${fmtAgo(when)}` : ''}
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Subsystem cards grouped by tier */}
                {['core', 'pipeline', 'resource'].map(group => {
                  const items = (healthLatest.checks || []).filter(c => c.group === group);
                  if (!items.length) return null;
                  const label = { core: 'Core', pipeline: 'Pipelines', resource: 'Resources' }[group];
                  return (
                    <div className="ad-card" key={group} style={{ marginTop: 16 }}>
                      <h3>{label}</h3>
                      <div className="ad-health-grid">
                        {items.map(c => (
                          <div className={`ad-hc ad-hc-${c.status}`} key={c.name} title={c.error || c.detail || ''}>
                            <span className="ad-hc-dot" />
                            <div className="ad-hc-body">
                              <div className="ad-hc-name">
                                {c.name.replace(/_/g, ' ')}
                                <span className="ad-hc-ms">{c.latency_ms != null ? `${Math.round(c.latency_ms)}ms` : ''}</span>
                              </div>
                              <div className="ad-hc-detail">{c.detail || c.error || ''}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}

                {/* Uptime trend */}
                {healthHistory.length > 1 && (
                  <div className="ad-card" style={{ marginTop: 16 }}>
                    <h3>Recent sweeps <span style={{ fontWeight: 400, color: 'var(--faint)', fontSize: 12 }}>· newest on the right</span></h3>
                    <div className="ad-health-trend">
                      {healthHistory.slice().reverse().map(r => (
                        <i key={r.id} className={`ad-htick ad-htick-${r.overall_status}`}
                          title={`${r.overall_status} · ${r.tier} · ${r.down_count} down / ${r.degraded_count} degraded · ${r.run_at ? new Date(r.run_at).toLocaleString() : ''}`} />
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ═══ A/B TUNING VIEW ═══ */}
        {view === 'tuning' && (
          <div className="ad-view">
            <div className="ad-view-header">
              <h1>A/B Tuning</h1>
              <button className="ad-refresh-btn" onClick={fetchTuneHistory} disabled={tuneLoading}>
                {tuneLoading ? 'Loading…' : 'Refresh'}
              </button>
            </div>

            <p style={{ color: '#666', marginTop: -4, marginBottom: 16, maxWidth: 640 }}>
              Retrieval-threshold knobs swept against the eval harness. The live value is the
              one prod currently serves; each bar is a candidate’s mean overall quality. A candidate
              only wins if it beats the live value without regressing any dimension.
            </p>

            {(!tuneKnobs || Object.keys(tuneKnobs).length === 0) ? (
              <div className="ad-empty">No A/B sweeps recorded yet.</div>
            ) : (
              Object.entries(tuneKnobs).map(([knob, rows]) => {
                const current = rows.find((r) => r.is_current);
                const best = rows.reduce((a, b) => ((b.mean_overall || 0) > (a.mean_overall || 0) ? b : a), rows[0]);
                const maxScore = Math.max(...rows.map((r) => r.mean_overall || 0), 0.001);
                return (
                  <div className="ad-card" key={knob} style={{ marginBottom: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                      <h3 style={{ textTransform: 'none' }}>{knob.replace(/_/g, ' ')}</h3>
                      <span style={{ color: '#666', fontSize: 13 }}>
                        live: <b>{current ? current.value : '—'}</b>
                        {best && current && best.value !== current.value &&
                          (best.mean_overall - current.mean_overall) > 0.02 &&
                          <span style={{ color: '#2e9e5b' }}> · try {best.value}</span>}
                      </span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}>
                      {rows.slice().sort((a, b) => a.value - b.value).map((r) => {
                        const pct = Math.round(((r.mean_overall || 0) / maxScore) * 100);
                        const scorePct = Math.round((r.mean_overall || 0) * 100);
                        const isLive = r.is_current;
                        const color = isLive ? '#2f6fed' : '#9bb0c9';
                        return (
                          <div key={r.value} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{ width: 70, fontVariantNumeric: 'tabular-nums' }}>
                              {r.value}{isLive && <span style={{ color: '#2f6fed', fontSize: 11 }}> ●live</span>}
                            </div>
                            <div style={{ flex: 1, background: '#eef1f5', borderRadius: 6, height: 16 }}>
                              <div style={{ width: `${pct}%`, background: color, height: '100%', borderRadius: 6 }} />
                            </div>
                            <div style={{ width: 44, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{scorePct}%</div>
                            <div style={{ width: 60, textAlign: 'right', color: '#888', fontSize: 12 }}>n={r.n_runs ?? '—'}</div>
                          </div>
                        );
                      })}
                    </div>
                    {rows.some((r) => r.note) && (
                      <div style={{ marginTop: 10, borderTop: '1px solid #eee', paddingTop: 8 }}>
                        {rows.filter((r) => r.note).map((r) => (
                          <div key={r.value} style={{ color: '#888', fontSize: 12 }}>
                            <b style={{ fontVariantNumeric: 'tabular-nums' }}>{r.value}</b> — {r.note}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* ═══ FAITHFULNESS EDITOR VIEW ═══ */}
        {view === 'editor' && (
          <div className="ad-view">
            <div className="ad-view-header">
              <h1>Faithfulness Editor</h1>
              <button className="ad-refresh-btn" onClick={fetchEditorConfig} disabled={editorLoading}>
                {editorLoading ? 'Loading…' : 'Refresh'}
              </button>
            </div>

            <p style={{ color: '#666', marginTop: -4, marginBottom: 16, maxWidth: 680 }}>
              Story’s reply passes through a strict “remove anything not in the memories” editor
              before it’s sent. The self-hosted model can’t reliably strip the vivid detail it
              invents, so you can route <b>just that editor pass</b> to a stronger Claude model —
              billed against a Claude subscription via OAuth, not a paid API key. The person Story
              speaks as is unchanged; only the fact-check editor gets stronger. If Claude is ever
              unreachable, it falls back to the self-hosted editor automatically.
            </p>

            <div className="ad-card" style={{ maxWidth: 680 }}>
              {/* Re-auth alarm: the token's refresh chain died and the loop can't self-heal */}
              {editorCfg?.needs_reauth && (
                <div style={{ background: '#fdecec', border: '1px solid #e5484d', color: '#b42318', borderRadius: 8, padding: '10px 12px', marginBottom: 14, fontWeight: 600 }}>
                  ⚠️ The editor's Claude token needs a re-auth — Story has fallen back to the weaker local editor. Click <em>Generate token</em> below to restore it.
                  {editorCfg?.last_error ? (
                    <div style={{ fontWeight: 400, color: '#8a1c1c', marginTop: 4, fontSize: 12 }}>Last error: {String(editorCfg.last_error).slice(0, 160)}</div>
                  ) : null}
                </div>
              )}
              {/* Current status */}
              <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 16 }}>
                <div>
                  <div className="ad-sys-title">Status</div>
                  <div style={{ fontWeight: 700, color: editorCfg?.active ? '#2e9e5b' : '#888' }}>
                    {editorCfg?.active ? '● Claude editor active' : '○ Self-hosted editor'}
                  </div>
                </div>
                <div>
                  <div className="ad-sys-title">OAuth token</div>
                  <div style={{ color: '#555' }}>
                    {editorCfg?.oauth_configured
                      ? (editorCfg.oauth_expires_at ? `expires ${new Date(editorCfg.oauth_expires_at).toLocaleString()}` : 'present')
                      : 'not set'}
                    {editorCfg?.oauth_configured && (
                      <div style={{ fontSize: 12, color: editorCfg?.needs_reauth ? '#b42318' : '#2e9e5b', marginTop: 2 }}>
                        {editorCfg?.needs_reauth ? '⚠ auto-refresh: needs re-auth' : '🔄 auto-refresh on'}
                        {editorCfg?.last_refresh_ok ? ` · last ok ${new Date(editorCfg.last_refresh_ok * 1000).toLocaleTimeString()}` : ''}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Provider */}
              <div style={{ marginBottom: 14 }}>
                <label className="ad-sys-title" htmlFor="ed-provider">Editor</label>
                <select id="ed-provider" value={editorForm.provider}
                  onChange={(e) => setEditorForm({ ...editorForm, provider: e.target.value })}
                  style={{ display: 'block', marginTop: 4, padding: '8px 10px', minWidth: 260 }}>
                  <option value="off">Self-hosted Soul LM (default)</option>
                  <option value="anthropic">Claude (subscription OAuth)</option>
                </select>
              </div>

              {/* Model */}
              {editorForm.provider === 'anthropic' && (
                <div style={{ marginBottom: 14 }}>
                  <label className="ad-sys-title" htmlFor="ed-model">Claude model</label>
                  <select id="ed-model" value={editorForm.model}
                    onChange={(e) => setEditorForm({ ...editorForm, model: e.target.value })}
                    style={{ display: 'block', marginTop: 4, padding: '8px 10px', minWidth: 260 }}>
                    {(editorCfg?.models || ['claude-haiku-4-5', 'claude-sonnet-5', 'claude-opus-4-8']).map((m) => (
                      <option key={m} value={m}>{m}{m === 'claude-haiku-4-5' ? ' — fast, cheap (recommended)' : m === 'claude-sonnet-5' ? ' — stronger' : ' — strongest'}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* One-click OAuth generate flow */}
              {editorForm.provider === 'anthropic' && (
                <div style={{ borderTop: '1px solid #eee', paddingTop: 12, marginBottom: 8 }}>
                  <div className="ad-sys-title" style={{ marginBottom: 6 }}>Generate a token (recommended)</div>
                  {!oauthUrl ? (
                    <button className="ad-refresh-btn" onClick={startOauth} disabled={oauthBusy}>
                      {oauthBusy ? 'Starting…' : '🔗 Generate login link'}
                    </button>
                  ) : (
                    <div>
                      <div style={{ marginBottom: 8 }}>
                        1. <a href={oauthUrl} target="_blank" rel="noopener noreferrer" style={{ fontWeight: 600 }}>
                          Open the Claude login page ↗
                        </a> — approve, then copy the code it shows you.
                      </div>
                      <div style={{ marginBottom: 6 }}>2. Paste the code here:</div>
                      <input type="text" autoComplete="off" placeholder="paste code  (looks like abc123#xyz…)"
                        value={oauthCode} onChange={(e) => setOauthCode(e.target.value)}
                        style={{ display: 'block', width: '100%', padding: '8px 10px', marginBottom: 8, boxSizing: 'border-box' }} />
                      <div style={{ display: 'flex', gap: 10 }}>
                        <button className="ad-refresh-btn" onClick={finishOauth} disabled={oauthBusy || !oauthCode.trim()}>
                          {oauthBusy ? 'Exchanging…' : 'Complete authorization'}
                        </button>
                        <button className="ad-btn-sm" onClick={() => { setOauthUrl(''); setOauthSession(''); setOauthCode(''); }}>Cancel</button>
                      </div>
                    </div>
                  )}
                  <div style={{ color: '#999', fontSize: 12, marginTop: 6 }}>
                    Opens Anthropic’s OAuth login in a new tab. On approval it returns a one-time code you paste back — we exchange it for tokens and activate the editor. Billed against your Claude subscription.
                  </div>
                </div>
              )}

              {/* OAuth tokens (write-only) — manual fallback */}
              {editorForm.provider === 'anthropic' && (
                <div style={{ borderTop: '1px solid #eee', paddingTop: 12, marginBottom: 8 }}>
                  <div className="ad-sys-title" style={{ marginBottom: 6 }}>
                    Or paste tokens manually {editorCfg?.oauth_configured ? '(leave blank to keep current)' : '(from an existing Claude Code login)'}
                  </div>
                  <input type="password" autoComplete="off" placeholder="access token  (sk-ant-oat01-…)"
                    value={editorForm.access_token}
                    onChange={(e) => setEditorForm({ ...editorForm, access_token: e.target.value })}
                    style={{ display: 'block', width: '100%', padding: '8px 10px', marginBottom: 8, boxSizing: 'border-box' }} />
                  <input type="password" autoComplete="off" placeholder="refresh token  (sk-ant-ort01-…)  — enables auto-refresh"
                    value={editorForm.refresh_token}
                    onChange={(e) => setEditorForm({ ...editorForm, refresh_token: e.target.value })}
                    style={{ display: 'block', width: '100%', padding: '8px 10px', marginBottom: 8, boxSizing: 'border-box' }} />
                  <input type="number" placeholder="expires_in seconds (optional, e.g. 28800)"
                    value={editorForm.expires_in}
                    onChange={(e) => setEditorForm({ ...editorForm, expires_in: e.target.value })}
                    style={{ display: 'block', width: '100%', padding: '8px 10px', boxSizing: 'border-box' }} />
                  <div style={{ color: '#999', fontSize: 12, marginTop: 6 }}>
                    Tokens are write-only — stored server-side and never shown back. The refresh token rotates on each refresh; the server persists the new one.
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12 }}>
                <button className="ad-refresh-btn" onClick={saveEditorConfig} disabled={editorSaving}>
                  {editorSaving ? 'Saving…' : 'Save & verify'}
                </button>
                {editorMsg && <span style={{ fontSize: 13 }}>{editorMsg}</span>}
              </div>
            </div>
          </div>
        )}

        {/* ═══ SYSTEM VIEW ═══ */}
        {view === 'system' && (
          <div className="ad-view">
            <div className="ad-view-header">
              <h1>System</h1>
            </div>

            <div className="ad-sys-grid">
              <div className="ad-sys-card">
                <div className="ad-sys-title">Environment</div>
                <div className="ad-sys-rows">
                  <div className="ad-sys-row"><span>API URL</span><span className="ad-sys-val">{API_BASE || window.location.origin}</span></div>
                  <div className="ad-sys-row"><span>Frontend</span><span className="ad-sys-val">{window.location.origin}</span></div>
                  <div className="ad-sys-row"><span>Node Env</span><span className="ad-sys-val">{process.env.NODE_ENV}</span></div>
                  <div className="ad-sys-row"><span>Build</span><span className="ad-sys-val">React {React.version}</span></div>
                </div>
              </div>

              <div className="ad-sys-card">
                <div className="ad-sys-title">Platform Totals</div>
                <div className="ad-sys-rows">
                  <div className="ad-sys-row"><span>Users</span><span className="ad-sys-val">{s.total_users ?? '—'}</span></div>
                  <div className="ad-sys-row"><span>Messages</span><span className="ad-sys-val">{s.total_messages ?? '—'}</span></div>
                  <div className="ad-sys-row"><span>Memories</span><span className="ad-sys-val">{s.total_memories ?? '—'}</span></div>
                  <div className="ad-sys-row"><span>Sessions</span><span className="ad-sys-val">{s.total_sessions ?? '—'}</span></div>
                  <div className="ad-sys-row"><span>Share Links</span><span className="ad-sys-val">{s.share_links_count ?? '—'}</span></div>
                  <div className="ad-sys-row"><span>Geo Memories</span><span className="ad-sys-val">{s.geo_memories_count ?? '—'}</span></div>
                </div>
              </div>

              <div className="ad-sys-card">
                <div className="ad-sys-title">Activity (24h)</div>
                <div className="ad-sys-rows">
                  <div className="ad-sys-row"><span>New Users</span><span className="ad-sys-val">{s.new_users_24h ?? '—'}</span></div>
                  <div className="ad-sys-row"><span>Messages</span><span className="ad-sys-val">{s.messages_24h ?? '—'}</span></div>
                  <div className="ad-sys-row"><span>Active Sessions</span><span className="ad-sys-val">{s.active_sessions_24h ?? '—'}</span></div>
                </div>
              </div>

              <div className="ad-sys-card">
                <div className="ad-sys-title">Quick Actions</div>
                <div className="ad-sys-actions">
                  <button className="ad-sys-action-btn" onClick={() => { fetchStats(); fetchUsers(); }}>Refresh All Data</button>
                  <button className="ad-sys-action-btn" onClick={() => setView('users')}>Manage Users</button>
                  <button className="ad-sys-action-btn" onClick={() => navigate('/app')}>Return to App</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* ═══ MODALS ═══ */}
      {pwModal && (
        <div className="ad-modal-overlay" onClick={() => setPwModal(null)}>
          <div className="ad-modal" onClick={e => e.stopPropagation()}>
            <h2>Set Password</h2>
            <p className="ad-modal-email">{pwModal.email}</p>
            <p className="ad-modal-uid">UID: {pwModal.uid}</p>
            <input className="ad-modal-input" type="text" placeholder="New password" value={newPassword} onChange={e => setNewPassword(e.target.value)} autoFocus />
            <div className="ad-modal-actions">
              <button className="ad-btn-sm" onClick={() => setPwModal(null)}>Cancel</button>
              <button className="ad-btn-sm ad-btn-primary" onClick={handleSetPassword} disabled={settingPw || !newPassword}>
                {settingPw ? 'Setting…' : 'Set Password'}
              </button>
            </div>
          </div>
        </div>
      )}

      {emailModal && (
        <div className="ad-modal-overlay" onClick={() => setEmailModal(null)}>
          <div className="ad-modal" onClick={e => e.stopPropagation()}>
            <h2>Edit Email</h2>
            <p className="ad-modal-email">{emailModal.email}</p>
            <p className="ad-modal-uid">UID: {emailModal.uid}</p>
            <input className="ad-modal-input" type="email" placeholder="New email address" value={newEmail} onChange={e => setNewEmail(e.target.value)} autoFocus />
            <div className="ad-modal-actions">
              <button className="ad-btn-sm" onClick={() => setEmailModal(null)}>Cancel</button>
              <button className="ad-btn-sm ad-btn-primary" onClick={handleUpdateEmail} disabled={settingEmail || !newEmail || newEmail === emailModal.email}>
                {settingEmail ? 'Updating…' : 'Update Email'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ COMPOSE EMAIL MODAL (grafted from prod) ═══ */}
      {emailComposeModal && (
        <div className="ad-modal-overlay" onClick={() => setEmailComposeModal(null)}>
          <div className="ad-modal ad-modal-wide" onClick={e => e.stopPropagation()}>
            <h2>Compose Email</h2>
            <p className="ad-modal-uid" style={{ marginBottom: 14 }}>Sent via the branded base email template.</p>
            <label className="ad-compose-label">To</label>
            <input
              className="ad-modal-input"
              type="email"
              placeholder="recipient@example.com"
              value={emailComposeModal.to}
              onChange={e => setEmailComposeModal({ to: e.target.value })}
              autoFocus={!emailComposeModal.to}
            />
            <label className="ad-compose-label">Subject</label>
            <input
              className="ad-modal-input"
              type="text"
              placeholder="Subject"
              value={composeSubject}
              onChange={e => setComposeSubject(e.target.value)}
              autoFocus={!!emailComposeModal.to}
            />
            <label className="ad-compose-label">Message</label>
            <textarea
              className="ad-modal-input ad-modal-textarea"
              placeholder="Plain text or HTML…"
              value={composeMessage}
              onChange={e => setComposeMessage(e.target.value)}
              rows={8}
            />
            <div className="ad-modal-actions">
              <button className="ad-btn-sm" onClick={() => setEmailComposeModal(null)}>Cancel</button>
              <button
                className="ad-btn-sm ad-btn-primary"
                onClick={handleSendEmail}
                disabled={sendingEmail || !emailComposeModal.to || !composeSubject || !composeMessage}
              >
                {sendingEmail ? 'Sending…' : 'Send Email'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminPanel;
